import type { IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import type { GatingReasonEnum } from "#graphql/supergraphTypes";
import type {
  CheckItem,
  DocumentSection,
  PackageDocument,
  PackageEnumerationStatus,
} from "../viewModelTypes";

/** Status for a section/document accordion header — mirrors `SectionAccordion`
 *  (statusText + statusVariant + statusIconType). `variant` is the DS
 *  AccordionStatusVariant; open issues read as a red "Warning". */
export type SectionStatus = {
  text: string;
  variant: "success" | "error" | "neutral";
  icon: IconType;
};

/** Result badge for a single check row — icon + toned text, matching
 *  `StatusBadge`. `tone` maps to the DS color in the badge. */
export type CheckBadge = {
  text: string;
  icon: IconType;
  tone: "success" | "warning" | "danger" | "neutral";
};

/**
 * Whether a check is a live issue, i.e. counts toward "not green". Triage-aware
 * and derived from the FE state (not the backend `isOpen`, which does not flip on
 * triage): an ignored check is resolved, a confirmed one is an issue, and an
 * untriaged one is an issue iff it failed.
 */
export function isIssue(check: CheckItem): boolean {
  if (check.triageState === "IGNORED") {
    return false;
  }
  if (check.triageState === "CONFIRMED_AS_ISSUE") {
    return true;
  }
  return check.outcome === "FAIL";
}

/**
 * Rolls a set of checks up to one accordion status: any live issue → Warning,
 * an all-skipped group → Skipped, everything else → No issues.
 */
export function sectionStatus(checks: readonly CheckItem[]): SectionStatus {
  if (checks.some(isIssue)) {
    return { text: "Warning", variant: "error", icon: "exclamation-triangle" };
  }
  if (checks.length > 0 && checks.every((c) => c.outcome === "SKIPPED")) {
    return { text: "Skipped", variant: "neutral", icon: "minus-circle" };
  }
  return { text: "No issues", variant: "success", icon: "check" };
}

/**
 * Document-level status: rolls up every check across its sections, unless the
 * document was gated — its checks never ran, so a gated document reads as
 * "Error" regardless of what its (all-SKIPPED) checks would roll up to on their
 * own. Gated behind `fayMVPAugust` (mirrors `packageStatus`); off, a gated
 * document rolls up from its checks alone, blind to `gatingReason`.
 */
export function documentStatus(
  sections: readonly DocumentSection[],
  gatingReason: GatingReasonEnum | null | undefined,
  fayMVPAugust: boolean,
): SectionStatus {
  if (fayMVPAugust && gatingReason != null) {
    return { text: "Error", variant: "error", icon: "exclamation-triangle" };
  }
  return sectionStatus(sections.flatMap((s) => s.checks));
}

/**
 * Canned per-reason messages a gated document shows in place of its
 * skipped-checks breakdown. Bucket A reasons (cover page / fund type /
 * period-end) name the missing item and point to re-upload; bucket B reasons
 * (section detection / FSDocument creation, plus the `UNKNOWN` fallback so an
 * unrecognized reason doesn't wrongly blame the document) own it as an error on
 * our end.
 */
const SYSTEM_GATING_MESSAGE =
  "We couldn't process this document because of an error on our end, so the checks didn't run. Try re-uploading — if it keeps failing, contact support. This is a Juniper Square issue, not a problem with your document.";

const GATING_REASON_MESSAGES: Record<GatingReasonEnum, string> = {
  NO_COVER_PAGE:
    "We couldn't find a cover page on this document, so the review couldn't start. Confirm you've uploaded the complete financial statements — including the cover page — then re-upload.",
  FUND_TYPE_EXTRACTION_FAILED:
    "We couldn't identify the fund type from the cover page. Only LP and LLC financial statements are supported right now. Confirm the cover page states the entity's legal type as LP or LLC, then re-upload.",
  PERIOD_END_DATE_EXTRACTION_FAILED:
    "We couldn't read a reporting period-end date from this document. Confirm the period-end date is stated on the statements, then re-upload.",
  SECTION_DETECTION_FAILED: SYSTEM_GATING_MESSAGE,
  FS_DOCUMENT_CREATION_FAILED: SYSTEM_GATING_MESSAGE,
  UNKNOWN: SYSTEM_GATING_MESSAGE,
};

/**
 * The canned string for a gated document's `gatingReason`. Falls back to the
 * system-error string for a value outside today's enum — the lookup is
 * exhaustive against `GatingReasonEnum` at compile time, but the backend enum can
 * add a member before this package's generated types catch up, and an
 * unrecognized reason shouldn't render a description-less Banner.
 */
export function gatingMessage(reason: GatingReasonEnum): string {
  return GATING_REASON_MESSAGES[reason] ?? SYSTEM_GATING_MESSAGE;
}

/**
 * The result badge for one check row. The reviewer's triage decision wins once
 * made (Issue / Ignored); otherwise the raw DDG outcome drives it.
 */
export function checkBadge(check: CheckItem): CheckBadge {
  if (check.triageState === "CONFIRMED_AS_ISSUE") {
    return { text: "Issue", icon: "exclamation-triangle", tone: "danger" };
  }
  if (check.triageState === "IGNORED") {
    return { text: "Ignored", icon: "minus-circle", tone: "neutral" };
  }
  switch (check.outcome) {
    case "FAIL":
      return { text: "Warning", icon: "exclamation-triangle", tone: "warning" };
    case "SKIPPED":
      return { text: "Skipped", icon: "minus-circle", tone: "neutral" };
    case "PASS":
      return { text: "No issues", icon: "check", tone: "success" };
    default:
      return { text: check.outcome, icon: "minus-circle", tone: "neutral" };
  }
}

const ACRONYMS = new Set(["ytd", "itd", "lp", "nav", "toc", "pcap", "soi"]);
const SMALL_WORDS = new Set(["of", "and", "to", "the", "for", "a", "an"]);

/**
 * Humanizes a raw backend label (section or check name) for display — e.g.
 * `statement_of_partners_capital` → "Statement of Partners Capital". Strings that
 * already contain a space are treated as pre-formatted and returned untouched.
 */
export function humanizeLabel(raw: string): string {
  if (raw.includes(" ")) {
    return raw;
  }
  return raw
    .split("_")
    .filter(Boolean)
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) {
        return lower.toUpperCase();
      }
      if (i > 0 && SMALL_WORDS.has(lower)) {
        return lower;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

/** "3 checks" / "1 check" for an accordion subtitle. */
export function checkCountLabel(count: number): string {
  return `${count} ${count === 1 ? "check" : "checks"}`;
}

/** Package-header roll-up stats (Status / Checks passed / triage breakdown / Documents). */
export type PackageSummary = {
  documentCount: number;
  /** Failed checks still awaiting a triage decision. */
  requireReviewCount: number;
  /** Checks the reviewer has confirmed as an issue. */
  confirmedIssueCount: number;
  /** Checks that ran and are OK (ran minus open issues). */
  passed: number;
  /** Checks that ran, i.e. produced a pass/fail result (skipped excluded). */
  total: number;
};

/**
 * Rolls a package's documents up to the header stats. "Ran" excludes SKIPPED
 * checks; `requireReviewCount` + `confirmedIssueCount` together are the
 * open-check count (post-triage) — split by triage state so the header shows
 * triage progress instead of one flat number. `passed` is the ran checks that
 * aren't open — so "passed of total" reads like "10 of 19".
 *
 * A gated document contributes none of its checks here — its checks never ran at
 * all, so counting them (even as excluded "skipped") would still misrepresent the
 * package's real review progress. Gated behind `fayMVPAugust` (mirrors
 * `packageStatus`); off, every document's checks are counted. `documentCount` is
 * unaffected either way: it counts documents in the package, not checks that ran.
 */
export function packageSummary(
  documents: readonly PackageDocument[],
  fayMVPAugust: boolean,
): PackageSummary {
  const checks = documents
    .filter((doc) => !fayMVPAugust || doc.gatingReason == null)
    .flatMap((doc) => doc.sections.flatMap((s) => s.checks));
  const ran = checks.filter((c) => c.outcome !== "SKIPPED");
  const issues = ran.filter(isIssue).length;
  const confirmedIssueCount = ran.filter((c) => c.triageState === "CONFIRMED_AS_ISSUE").length;
  const requireReviewCount = issues - confirmedIssueCount;
  return {
    documentCount: documents.length,
    requireReviewCount,
    confirmedIssueCount,
    total: ran.length,
    passed: ran.length - issues,
  };
}

/**
 * The package's review state. Four states, highest-priority first — see
 * `packageStatus` for the waterfall itself.
 *
 * Mirrors the backend `CoverageCellState` values that a *packaged* grid cell can
 * hold, so the cell and this stat can't disagree. The cell additionally has
 * EMPTY / EMPTY_PACKAGE / MISSING_TARGET_DOC, which describe whether there is a
 * package to review at all — by the time the slideout is open there is one, so
 * they have no counterpart here.
 */
export type PackageStatus =
  | "CHECKS_RUNNING"
  | "REQUIRES_REVIEW"
  | "CONFIRMED_ISSUES"
  | "APPROVED";

/**
 * Rolls a package up to its review state. The single source of truth behind the
 * slideout's Status stat; fay's `derive_cell_state` applies the same waterfall
 * server-side for the coverage grid cell.
 *
 * Waterfall — highest-priority state wins:
 *   1. any check still running                     -> CHECKS_RUNNING
 *   2. else >=1 Fay-flagged, un-triaged line item  -> REQUIRES_REVIEW
 *   3. else >=1 line item confirmed as an issue    -> CONFIRMED_ISSUES
 *   4. else (every line item passed or ignored)    -> APPROVED
 *
 * REQUIRES_REVIEW outranking CONFIRMED_ISSUES is the point of the ordering, not
 * an accident: the status holds at "requires review" until every check is
 * resolved, so marking the first of ten findings as an issue must not flip the
 * package while nine still await a decision.
 *
 * APPROVED is deliberately the *fall-through*, reachable only once nothing is
 * untriaged and nothing is confirmed. Before this waterfall the stat went green
 * on "0 require review" alone, so a package with 14 confirmed issues read as
 * passed — a real reported period-close. Green must never hide confirmed issues.
 *
 * A null `enumerationStatus` is not treated as running: the field is absent on
 * an older supergraph, and inferring "running" from its absence would pin every
 * package at CHECKS_RUNNING. The checks themselves still decide.
 *
 * `fayMVPAugust` is the release gate. Off: reproduces the pre-waterfall read
 * exactly — a flat "any open issue" boolean collapsed onto
 * REQUIRES_REVIEW/APPROVED, blind to `enumerationStatus` and to the
 * untriaged/confirmed distinction, i.e. the literal shape of the bug the
 * waterfall fixes. On: the waterfall above applies.
 */
export function packageStatus(
  documents: readonly PackageDocument[],
  enumerationStatus: PackageEnumerationStatus | null,
  fayMVPAugust: boolean,
): PackageStatus {
  const { requireReviewCount, confirmedIssueCount } = packageSummary(documents, fayMVPAugust);
  if (!fayMVPAugust) {
    return requireReviewCount + confirmedIssueCount > 0 ? "REQUIRES_REVIEW" : "APPROVED";
  }
  if (enumerationStatus === "RUNNING") {
    return "CHECKS_RUNNING";
  }
  if (requireReviewCount > 0) {
    return "REQUIRES_REVIEW";
  }
  if (confirmedIssueCount > 0) {
    return "CONFIRMED_ISSUES";
  }
  return "APPROVED";
}

/** How a `PackageStatus` renders in the slideout's Status stat. */
export type PackageStatusPresentation = {
  label: string;
  icon: IconType;
  color: Colors;
};

/**
 * Presentation per review state. Confirmed issues is red and No issues is
 * green per the design pass; Requires review keeps its existing red treatment,
 * and Checks running takes a neutral one. Giving the two intermediate states
 * their own colors (amber for Requires review, grey for Checks running) is a
 * proposed follow-up — so today Requires review and Confirmed issues share the
 * same icon and color and are distinguished by label text alone.
 */
export const PACKAGE_STATUS_PRESENTATION: Record<PackageStatus, PackageStatusPresentation> = {
  CHECKS_RUNNING: { label: "Checks running", icon: "clock", color: Colors.Neutral600 },
  REQUIRES_REVIEW: {
    label: "Requires review",
    icon: "exclamation-triangle",
    color: Colors.Danger500,
  },
  CONFIRMED_ISSUES: {
    label: "Confirmed issues",
    icon: "exclamation-triangle",
    color: Colors.Danger500,
  },
  APPROVED: { label: "No issues", icon: "check", color: Colors.Success500 },
};

/**
 * Pre-waterfall presentation for REQUIRES_REVIEW with `fayMVPAugust` off — same
 * icon/color as today, only the label text changes ("Has issues" vs "Requires
 * review"). APPROVED needs no legacy entry: its label ("No issues") was never
 * renamed, so the flag-on and flag-off reads are identical.
 */
const LEGACY_PACKAGE_STATUS_PRESENTATION: Record<"REQUIRES_REVIEW", PackageStatusPresentation> =
  {
    REQUIRES_REVIEW: {
      label: "Has issues",
      icon: "exclamation-triangle",
      color: Colors.Danger500,
    },
  };

/**
 * Presentation for a `PackageStatus`, flag-aware. Off: the pre-waterfall copy
 * for REQUIRES_REVIEW — the only state whose label the flag actually changes;
 * every other state (including APPROVED, and the CHECKS_RUNNING/
 * CONFIRMED_ISSUES `packageStatus` never returns with the flag off) falls
 * through to the current map unchanged.
 */
export function getPackageStatusPresentation(
  status: PackageStatus,
  fayMVPAugust: boolean,
): PackageStatusPresentation {
  if (!fayMVPAugust && status === "REQUIRES_REVIEW") {
    return LEGACY_PACKAGE_STATUS_PRESENTATION.REQUIRES_REVIEW;
  }
  return PACKAGE_STATUS_PRESENTATION[status];
}
